import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import styles from "../../styles";
import { resolveRiskRatingColor } from "../../helpers/complianceExport";
import { formatDisplayValue } from "../../helpers";

const INPUT_FIELDS = [
    { key: "id", label: "ID" },
    { key: "status", label: "Status" },
    { key: "searchQuery", label: "Search Query (e.g., name)" },
    { key: "riskRating", label: "Risk Rating", type: "risk" },
    { key: "type", label: "Type" },
    { key: "dateOfBirth", label: "Date of Birth" },
    { key: "createdBy", label: "Created by" },
    { key: "createdOn", label: "Created on" },
    { key: "fuzziness", label: "Fuzziness" },
];

const RESULT_FIELDS = [
    { key: "findings", label: "Findings" },
    { key: "falsePositive", label: "False Positive" },
    { key: "timestamp", label: "Timestamp" },
];

const ComplianceTable = ({ t, rows = [], leading = null }) => {
    const renderInputHeader = () => (
        <View style={styles.complianceInputGroup}>
            {INPUT_FIELDS.map((field, index) => (
                <View
                    key={field.key}
                    style={{
                        ...styles.complianceHeaderCell,
                        ...styles.complianceInputHeaderCell,
                        ...(index === INPUT_FIELDS.length - 1 ? styles.borderRightNone : {}),
                    }}
                >
                    <Text style={styles.complianceHeaderText}>{t(field.label)}</Text>
                </View>
            ))}
        </View>
    );

    const renderResultHeader = () => (
        <View style={styles.complianceResultGroup}>
            {RESULT_FIELDS.map((field, index) => (
                <View
                    key={field.key}
                    style={{
                        ...styles.complianceHeaderCell,
                        ...styles.complianceResultHeaderCell,
                        ...(index === RESULT_FIELDS.length - 1 ? styles.borderRightNone : {}),
                    }}
                >
                    <Text style={styles.complianceHeaderText}>{t(field.label)}</Text>
                </View>
            ))}
        </View>
    );

    const renderInputCells = (searchInput = {}) => (
        <View style={styles.complianceInputGroup}>
            {INPUT_FIELDS.map((field, index) => (
                <View
                    key={field.key}
                    style={{
                        ...styles.complianceBodyCell,
                        ...styles.complianceInputBodyCell,
                        ...(index === INPUT_FIELDS.length - 1 ? styles.borderRightNone : {}),
                    }}
                >
                    {field.type === "risk" ? (
                        <View
                            style={{
                                ...styles.riskRatingDot,
                                backgroundColor: resolveRiskRatingColor(searchInput[field.key]),
                            }}
                        />
                    ) : (
                        <Text style={styles.complianceBodyText}>
                            {formatDisplayValue(searchInput[field.key])}
                        </Text>
                    )}
                </View>
            ))}
        </View>
    );

    const renderResultCells = (searchResult = {}) => (
        <View style={styles.complianceResultGroup}>
            {RESULT_FIELDS.map((field, index) => (
                <View
                    key={field.key}
                    style={{
                        ...styles.complianceBodyCell,
                        ...styles.complianceResultBodyCell,
                        ...(index === RESULT_FIELDS.length - 1 ? styles.borderRightNone : {}),
                    }}
                >
                    <Text style={styles.complianceBodyText}>
                        {formatDisplayValue(searchResult[field.key])}
                    </Text>
                </View>
            ))}
        </View>
    );

    const renderFindingsDetails = (findingsDetails = []) => {
        if (!findingsDetails.length) {
            return null;
        }

        return (
            <View style={styles.complianceFindingsBlock}>
                <Text style={styles.complianceFindingsTitle}>{t("Findings")}</Text>

                {findingsDetails.map((finding, index) => (
                    <View key={`finding-${index}`} style={styles.complianceFindingItem}>
                        {finding.title ? (
                            <Text style={styles.complianceFindingName}>{finding.title}</Text>
                        ) : null}
                        {finding.description ? (
                            <Text style={styles.complianceFindingDescription}>
                                {finding.description}
                            </Text>
                        ) : null}
                    </View>
                ))}
            </View>
        );
    };

    if (!rows.length) {
        return (
            <View>
                <View wrap={false} minPresenceAhead={140}>
                    {leading}
                    <View style={styles.complianceTableWrap}>
                        <View style={styles.complianceGroupHeaderRow}>
                            <View style={styles.complianceInputGroupHeader}>
                                <Text style={styles.complianceGroupHeaderText}>{t("Search Input")}</Text>
                            </View>
                            <View style={styles.complianceResultGroupHeader}>
                                <Text style={styles.complianceGroupHeaderText}>{t("Search Result")}</Text>
                            </View>
                        </View>
                        <View style={styles.complianceEmptyRow}>
                            <Text style={styles.complianceBodyText}>{t("No Data")}</Text>
                        </View>
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.complianceTableWrap}>
            <View wrap={false} minPresenceAhead={160}>
                {leading}
                <View style={styles.complianceGroupHeaderRow}>
                    <View style={styles.complianceInputGroupHeader}>
                        <Text style={styles.complianceGroupHeaderText}>{t("Search Input")}</Text>
                    </View>
                    <View style={styles.complianceResultGroupHeader}>
                        <Text style={styles.complianceGroupHeaderText}>{t("Search Result")}</Text>
                    </View>
                </View>
            </View>

            <View style={styles.complianceTable}>
                <View style={styles.complianceHeaderRow} wrap={false} minPresenceAhead={80}>
                    {renderInputHeader()}
                    {renderResultHeader()}
                </View>

                {rows.map((row, rowIndex) => (
                    <View key={`compliance-row-${rowIndex}`} wrap={false} minPresenceAhead={60}>
                        <View
                            style={{
                                ...styles.complianceDataRow,
                                ...(rowIndex === rows.length - 1 && !row.searchResult?.findingsDetails?.length
                                    ? styles.tableLastRow
                                    : {}),
                            }}
                        >
                            {row.isSubRow ? (
                                <>
                                    <View style={styles.complianceSubRowSpacer} />
                                    {renderResultCells(row.searchResult)}
                                </>
                            ) : (
                                <>
                                    {renderInputCells(row.searchInput)}
                                    {renderResultCells(row.searchResult)}
                                </>
                            )}
                        </View>

                        {!row.isSubRow ? renderFindingsDetails(row.searchResult?.findingsDetails) : null}
                    </View>
                ))}
            </View>
        </View>
    );
};

export default withNamespaces()(ComplianceTable);
